// src/pages/admin/AdminAddListing.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, doc, setDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { addListing } from '../../services/ListingsService';
import { sendListingApprovalEmail } from '../../services/EmailNotificationService';
import { uploadMultipleMedia } from '../../services/StorageService';
import MultiMediaUpload from '../../components/MultiMediaUpload';
import './Admin.css';

function AdminAddListing() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    location: '',
    isVirtual: false,
    providerEmail: '',
    providerName: '',
    imageUrl: '',
    media: [],
    isFreeEvent: false,
    isContactForPrice: false,
    maxAttendees: '',
    eventStart: '',
    eventEnd: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs with special logic for pricing
    if (type === 'checkbox') {
      let updates = { [name]: checked };
      
      // If isFreeEvent is checked, set price to 0 and uncheck isContactForPrice
      if (name === 'isFreeEvent' && checked) {
        updates = {
          ...updates,
          price: '0',
          isContactForPrice: false
        };
      }
      
      // If isContactForPrice is checked, set price to 0 and uncheck isFreeEvent
      if (name === 'isContactForPrice' && checked) {
        updates = {
          ...updates,
          price: '0',
          isFreeEvent: false
        };
      }
      
      setFormData(prev => ({ ...prev, ...updates }));
      return;
    }
    
    // If price is changed and not 0, uncheck both price-related checkboxes
    if (name === 'price' && value !== '0' && value !== '') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        isFreeEvent: false,
        isContactForPrice: false
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMediaSelected = (files) => {
    setMediaFiles(files);
    setUploadError(null);
  };

  const checkUserExists = async (email) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      // Emails may have been stored with different casing; try both spellings
      const candidates = normalizedEmail === email ? [email] : [email, normalizedEmail];
      for (const candidate of candidates) {
        const usersQuery = query(collection(db, 'users'), where('email', '==', candidate));
        const querySnapshot = await getDocs(usersQuery);
        if (!querySnapshot.empty) {
          return querySnapshot.docs[0];
        }
      }
      return null;
    } catch (err) {
      console.error('Error checking user existence:', err);
      return null;
    }
  };

  const createApprovalToken = async (email, listingId) => {
    try {
      const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
      await setDoc(doc(db, 'approvalTokens', token), {
        email: email.trim().toLowerCase(),
        listingId: listingId,
        type: 'listing_approval',
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
        used: false
      });
      return token;
    } catch (err) {
      console.error('Error creating approval token:', err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate event dates (optional; ensure end is not before start when both provided)
      let startDate = null;
      if (formData.eventStart) {
        const parsedStart = new Date(formData.eventStart);
        if (!isNaN(parsedStart)) {
          startDate = parsedStart;
        }
      }
      let endDate = null;
      if (formData.eventEnd) {
        const parsedEnd = new Date(formData.eventEnd);
        if (!isNaN(parsedEnd)) {
          endDate = parsedEnd;
        }
        if (startDate && endDate && endDate < startDate) {
          setError('Event end must be after the start.');
          setLoading(false);
          return;
        }
      }
      // Upload media files if provided
      let mediaData = [];
      
      if (mediaFiles.length > 0) {
        try {
          mediaData = await uploadMultipleMedia(mediaFiles);
        } catch (uploadErr) {
          console.error("Media upload error:", uploadErr);
          setError(uploadErr.message || "Failed to upload media. Please try again.");
          setLoading(false);
          return;
        }
      }

      // Check if user exists
      const existingUser = await checkUserExists(formData.providerEmail);

      const listingData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price: parseFloat(formData.price),
        location: formData.location,
        isVirtual: formData.isVirtual,
        imageUrl: mediaData.length > 0 ? mediaData[0].url : (formData.imageUrl || '/placeholder-listing.jpg'),
        media: mediaData,
        featured: false,
        contact: formData.providerEmail,  // Add the provider's email as contact
        isFreeEvent: formData.isFreeEvent,
        isContactForPrice: formData.isContactForPrice,
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null,
        eventStart: startDate,
        eventEnd: endDate
      };

      if (existingUser) {
        // User exists, create listing directly
        console.log('User exists:', existingUser.id, existingUser.data());
        const userId = existingUser.id;
        await addListing(listingData, userId);

        setSuccess(`Listing created successfully for existing user ${formData.providerEmail}`);
        setTimeout(() => {
          navigate('/admin/listings');
        }, 2000);
      } else {
        console.log('User does not exist, publishing listing and sending account setup email to:', formData.providerEmail);
        // User doesn't exist: publish the listing immediately. Ownership is
        // claimed when the provider creates their account via the emailed link.
        const listingId = await addListing({
          ...listingData,
          providerName: formData.providerName,
          pendingOwnerEmail: formData.providerEmail.trim().toLowerCase()
        }, null);

        // Create account-setup token pointing at the live listing
        const token = await createApprovalToken(formData.providerEmail, listingId);

        // Send account setup email
        try {
          const emailResult = await sendListingApprovalEmail({
            recipientEmail: formData.providerEmail,
            recipientName: formData.providerName,
            listingTitle: formData.title,
            approvalLink: `${window.location.origin}/approve-listing?token=${token}`,
            adminName: currentUser.displayName || 'RefoundEd Administrator'
          });
          
          console.log('Email sent successfully:', emailResult);
          
          // If using mock implementation in development
          if (emailResult.message && emailResult.message.includes('mock')) {
            console.log('📧 MOCK EMAIL SENT TO:', formData.providerEmail);
            console.log('📧 APPROVAL LINK:', `${window.location.origin}/approve-listing?token=${token}`);
          }
        } catch (emailError) {
          console.error('Failed to send email:', emailError);
          setError('Listing created but failed to send email. Please check the console.');
        }

        // Show approval link in development mode
        const isDevelopment = process.env.NODE_ENV === 'development';
        if (isDevelopment) {
          setSuccess(
            <div>
              <p>Listing published and account setup email sent to {formData.providerEmail}</p>
              <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                <strong>Development Mode - Approval Link:</strong><br />
                <a href={`/approve-listing?token=${token}`} target="_blank" rel="noopener noreferrer">
                  {window.location.origin}/approve-listing?token={token}
                </a>
              </p>
            </div>
          );
        } else {
          setSuccess(`Listing published and account setup email sent to ${formData.providerEmail}`);
        }
        setTimeout(() => {
          navigate('/admin/listings');
        }, 3000);
      }
    } catch (err) {
      console.error('Error creating listing:', err);
      setError('Failed to create listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Create Listing for User</h1>
        <p>Create a new listing on behalf of a service provider</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="admin-menu">
        <Link to="/admin" className="admin-menu-item">
          Applications
        </Link>
        <Link to="/admin/users" className="admin-menu-item">
          Users
        </Link>
        <Link to="/admin/listings" className="admin-menu-item active">
          Listings
        </Link>
      </div>

      <div className="admin-content">
        <div className="admin-form-container">
          <form onSubmit={handleSubmit} className="admin-form">
            <h2>Provider Information</h2>
            
            <div className="form-group">
              <label htmlFor="providerEmail">Provider Email *</label>
              <input
                type="email"
                id="providerEmail"
                name="providerEmail"
                value={formData.providerEmail}
                onChange={handleInputChange}
                required
                placeholder="provider@example.com"
              />
              <small>If the user doesn't exist, they'll receive an approval email</small>
            </div>

            <div className="form-group">
              <label htmlFor="providerName">Provider Name *</label>
              <input
                type="text"
                id="providerName"
                name="providerName"
                value={formData.providerName}
                onChange={handleInputChange}
                required
                placeholder="John Doe"
              />
            </div>

            <h2>Listing Details</h2>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter listing title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows="4"
                placeholder="Enter listing description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a category</option>
                <option value="education">Education</option>
                <option value="recreation">Recreation</option>
                <option value="childcare">Childcare</option>
                <option value="mentorship">Mentorship</option>
                <option value="professional_development">Professional Development</option>
                <option value="other">Other</option>
              </select>
            </div>

          <div className="form-group">
            <label>Event Date/Time (Optional)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label htmlFor="eventStart">Start</label>
                <input
                  type="datetime-local"
                  id="eventStart"
                  name="eventStart"
                  value={formData.eventStart}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label htmlFor="eventEnd">End (Optional)</label>
                <input
                  type="datetime-local"
                  id="eventEnd"
                  name="eventEnd"
                  value={formData.eventEnd}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
              Share any available schedule details now or leave both fields blank.
            </small>
          </div>

          
          <div className="form-group">
            <label>Price Options *</label>
              
              <div className="checkbox-group" style={{ marginBottom: '0.5rem' }}>
                <label>
                  <input
                    type="checkbox"
                    name="isFreeEvent"
                    checked={formData.isFreeEvent}
                    onChange={handleInputChange}
                  />
                  This is a free event
                </label>
              </div>
              
              <div className="checkbox-group" style={{ marginBottom: '0.5rem' }}>
                <label>
                  <input
                    type="checkbox"
                    name="isContactForPrice"
                    checked={formData.isContactForPrice}
                    onChange={handleInputChange}
                  />
                  Contact for pricing
                </label>
              </div>
              
              {!formData.isFreeEvent && !formData.isContactForPrice && (
                <div>
                  <label htmlFor="price">Price ($)</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required={!formData.isFreeEvent && !formData.isContactForPrice}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="location">Location *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                placeholder="City, Province"
              />
            </div>

            <div className="form-group">
              <label>Upload Images/Videos</label>
              <MultiMediaUpload 
                onMediaSelected={handleMediaSelected}
                existingMedia={[]}
              />
              {uploadError && (
                <small style={{ color: '#dc2626', marginTop: '0.25rem', display: 'block' }}>
                  {uploadError}
                </small>
              )}
              <small>You can also provide a direct image URL below (optional)</small>
              <input
                type="url"
                id="imageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                placeholder="https://example.com/image.jpg"
                style={{ marginTop: '0.5rem' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="maxAttendees">Maximum Attendees (Optional)</label>
              <input
                type="number"
                id="maxAttendees"
                name="maxAttendees"
                value={formData.maxAttendees}
                onChange={handleInputChange}
                min="1"
                placeholder="Leave empty for unlimited"
              />
              <small>Leave empty if there's no attendance limit</small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isVirtual"
                  checked={formData.isVirtual}
                  onChange={handleInputChange}
                />
                Virtual Service
              </label>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Listing'}
              </button>
              <Link to="/admin/listings" className="cancel-button">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminAddListing;
